import React from 'react'
import { useIntl } from 'react-intl'
import { useConfig } from 'msp-integrations'
import LineItemPromotion from '@molecules/LineItem/LineItemPromotion'
import { BonusDiscountLineItem } from '@molecules/LineItem/LineItemPromotion.types'
import { MiniCartTotalProps } from './MiniCart.types'

const PROMO_CODE_APPLIED = 'applied'
const PROMO_NO_APPLICABLE = 'no_applicable_promotion'

const MiniCartTotal = ({
  total,
  orderPriceAdjustments,
  removePromoCode,
  handleBonusDiscountLineItem = () => {},
  bonusItemsByCouponCode = new Map<string, BonusDiscountLineItem>(),
  couponItems = [],
  currency,
  inline,
  customerCreditUsedForItems
}: MiniCartTotalProps) => {
  const { formatMessage, formatNumber } = useIntl()
  const {
    tenantId,
    theme: { font }
  } = useConfig()
  const formatPrice = (price?: number) =>
    typeof price !== 'undefined' && currency
      ? formatNumber(price, {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'code'
        })
      : ''
  const couponsWithConditionsNotMet = couponItems.filter(
    (cI) =>
      (cI.statusCode !== PROMO_CODE_APPLIED &&
        !bonusItemsByCouponCode.has(cI.code)) ||
      !cI.valid
  )
  const couponsWithNotAppliedBonusProducts = couponItems.filter(
    (cI) =>
      cI.statusCode === PROMO_NO_APPLICABLE &&
      bonusItemsByCouponCode.has(cI.code) &&
      cI.valid
  )
  // @ToDo should be removed after GiftCard refactoring, using ProductLineItems instead of GiftCertLineItems
  const couponsOfWelcomeOfferApplied = couponItems.filter(
    (cI) =>
      cI.statusCode === PROMO_CODE_APPLIED &&
      cI.valid &&
      cI.code.startsWith('WO-') &&
      tenantId === 'jasmin'
  )

  const couponItemFromFriendReferralCampaign = couponItems.filter(
    (cI) =>
      cI.statusCode === PROMO_CODE_APPLIED && cI.valid && cI.c_fakeCouponCode
  )

  return (
    <div className='flex flex-col space-y-1 border-t-4 border-solid border-black'>
      <div className='mb-4 mt-0.5 border-t border-solid border-black' />
      {couponsOfWelcomeOfferApplied &&
        couponsOfWelcomeOfferApplied.length > 0 && (
          <LineItemPromotion
            priceAdjustments={orderPriceAdjustments}
            currency={currency}
            removePromoCode={removePromoCode}
            handleBonusDiscountLineItem={() => {}}
            couponItems={couponsOfWelcomeOfferApplied}
            bonusItemsByCouponCode={new Map()}
            inline={inline}
            headline={formatMessage({
              id: 'msp.cart.order.bonus.wo.label',
              defaultMessage: 'Welcome Offer Promotion'
            })}
            summary
          />
        )}
      {couponsWithNotAppliedBonusProducts &&
        couponsWithNotAppliedBonusProducts.length > 0 && (
          <LineItemPromotion
            priceAdjustments={orderPriceAdjustments}
            currency={currency}
            removePromoCode={removePromoCode}
            handleBonusDiscountLineItem={(
              bonusDiscountLineItem: BonusDiscountLineItem | undefined
            ) => handleBonusDiscountLineItem(bonusDiscountLineItem)}
            couponItems={couponsWithNotAppliedBonusProducts}
            bonusItemsByCouponCode={bonusItemsByCouponCode}
            inline={inline}
            headline={formatMessage({
              id: 'msp.cart.order.bonus.promotion.label',
              defaultMessage: 'Please select your bonus items'
            })}
            summary
          />
        )}
      {couponsWithConditionsNotMet &&
        couponsWithConditionsNotMet.length > 0 && (
          <LineItemPromotion
            priceAdjustments={orderPriceAdjustments}
            currency={currency}
            removePromoCode={removePromoCode}
            couponItems={couponsWithConditionsNotMet}
            inline={inline}
            headline={formatMessage({
              id: 'msp.cart.order.promotion.label',
              defaultMessage: 'Not applicable coupons'
            })}
            summary
          />
        )}
      {couponItemFromFriendReferralCampaign &&
        couponItemFromFriendReferralCampaign.length > 0 && (
          <LineItemPromotion
            priceAdjustments={orderPriceAdjustments}
            currency={currency}
            removePromoCode={removePromoCode}
            isFriendCoupon
            couponItems={couponItemFromFriendReferralCampaign}
            inline={inline}
            headline={formatMessage({
              id: 'msp.cart.order.friend_coupon.label',
              defaultMessage: 'Campaign: Refer a friend'
            })}
            summary
          />
        )}
      {orderPriceAdjustments && (
        <LineItemPromotion
          priceAdjustments={orderPriceAdjustments}
          currency={currency}
          removePromoCode={removePromoCode}
          couponItems={couponItems}
          inline={inline}
          headline={formatMessage({
            id: 'msp.cart.order.order.promotion.label',
            defaultMessage: 'Order promotions'
          })}
        />
      )}
      {customerCreditUsedForItems ? (
        <div className='text-14 flex flex-row justify-between px-5 pt-5'>
          <div>
            {formatMessage({
              id: 'msp.cart.customer_credit_used.items',
              defaultMessage: 'Item Credit'
            })}
          </div>
          <div className='font-bold'>
            {formatPrice(customerCreditUsedForItems)}
          </div>
        </div>
      ) : null}
      <div className='flex flex-row items-center justify-between p-5'>
        <div className='flex flex-col space-y-1'>
          <p className={font.regular}>
            {formatMessage({
              id: 'msp.cart.total.amount',
              defaultMessage: 'Total amount'
            })}
          </p>
          <div
            className={`${font.regular} text-grayRegular text-14 leading-16 tracking-0.4`}
          >
            {formatMessage({
              id: 'msp.cart.total.amount.vat',
              defaultMessage: 'incl. VAT, excl. shipping'
            })}
          </div>
          <div
            className={`${font.regular} text-grayRegular text-14 leading-16 tracking-0.4 pt-4`}
          >
            {formatMessage({
              id: 'msp.cart.shipment.info.2',
              defaultMessage: 'Free packaging and shipping from 70 EUR'
            })}
          </div>
        </div>
        <p className={font.bold}>{`${formatPrice(total)}`}</p>
      </div>
    </div>
  )
}

export default MiniCartTotal
